/**
 * The envelope every byte on the wire travels in.
 *
 * ```text
 * byte 0  magic 0x54, so a stray message from another protocol on the same channel is caught
 * byte 1  protocol version
 * byte 2  message kind
 * byte 3  flags, reserved and currently zero
 * byte 4+ payload, whatever the kind says it is
 * ```
 *
 * Four bytes of header rather than three, so the payload starts aligned and a snapshot writer can
 * lay out floats without a shifting offset. Decoding is all-or-nothing: a frame that fails any
 * check throws, and no caller ever sees a half-parsed message. That is the whole point of the
 * version byte, and it is why nothing in the game may put bytes on a channel itself.
 */

import { NetError } from '@/net/session/sessionErrors';
import { PROTOCOL_VERSION, negotiate, versionVerdictDetail } from '@/net/wire/protocolVersion';
import { isMessageKind } from '@/net/wire/messageKinds';
import type { MessageKind } from '@/net/wire/messageKinds';

export const FRAME_MAGIC = 0x54;
export const FRAME_HEADER_BYTES = 4;

export interface DecodedFrame {
  readonly version: number;
  readonly kind: MessageKind;
  readonly flags: number;
  /** A view into the received buffer rather than a copy. Valid for the callback that got it. */
  readonly payload: Uint8Array;
}

export function encodeFrame(
  kind: MessageKind,
  payload: Uint8Array,
  version: number = PROTOCOL_VERSION,
): Uint8Array<ArrayBuffer> {
  const frame = new Uint8Array(new ArrayBuffer(FRAME_HEADER_BYTES + payload.byteLength));
  frame[0] = FRAME_MAGIC;
  frame[1] = version;
  frame[2] = kind;
  frame[3] = 0;
  frame.set(payload, FRAME_HEADER_BYTES);
  return frame;
}

/** Throws `NetError`, `malformedFrame` or `versionMismatch`. Never returns a partial frame. */
export function decodeFrame(bytes: ArrayBuffer | Uint8Array): DecodedFrame {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);

  if (view.byteLength < FRAME_HEADER_BYTES) {
    throw new NetError(
      'malformedFrame',
      `${String(view.byteLength)} bytes is shorter than a header`,
    );
  }

  const [magic, version, kind, flags] = view;
  if (magic !== FRAME_MAGIC || version === undefined || kind === undefined || flags === undefined) {
    throw new NetError('malformedFrame', 'the frame does not start with this protocol');
  }

  const verdict = negotiate(version);
  if (verdict !== 'ok') {
    throw new NetError('versionMismatch', versionVerdictDetail(verdict, version));
  }

  if (!isMessageKind(kind)) {
    throw new NetError(
      'malformedFrame',
      `message kind ${String(kind)} is not one this build sends`,
    );
  }

  return { version, kind, flags, payload: view.subarray(FRAME_HEADER_BYTES) };
}
